import React, { useState, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { debounce } from 'lodash'
import DeleteIcon from "@mui/icons-material/Delete";
import DeactivateIcon from "@mui/icons-material/Block";
import { useDeactivateUsersDoctor, useDeleteUsersDoctor, useGetAllUsers } from "../../api/ekoApi";
import { useNavigate } from "react-router-dom";
import { AdminTabs } from "../../components/AdminTabs";
import { SearchInput } from "../../components/SearchInput";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const TabAndSearchWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  padding-right: 10px;
  align-items: center;
`;

const AllUsersList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data, isLoading, isError, refetch } = useGetAllUsers(searchTerm);
  const { t } = useTranslation()
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const isActive = useDeactivateUsersDoctor();
  const { mutate } = useDeleteUsersDoctor();

  useEffect(() => {
    if (currentTab === 1) {
      navigate("/alluserstable");
    }
    if(currentTab === 2){
      navigate("/allpatientstable");
    }
    if(currentTab === 0){
      navigate("/admintable")
    }
  }, [currentTab, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value, refetch) => {
        refetch({ searchTerm: value });
    }, 400),
    []
);

const handleSearchInput = (e) => {
  const value = e.target.value;
  setSearchTerm(value);

  handleSearch(value, refetch);
};

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleDelete = (rowData) => {
    setDeleteModalOpen(true);
    setSelectedRowData(rowData);
  };

  const handleDeactivate = (rowData) => {
    setDeactivateModalOpen(true);
    setSelectedRowData(rowData);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRowData(null);
  };

  const handleCloseDeactivateModal = () => {
    setDeactivateModalOpen(false);
    setSelectedRowData(null);
  };


  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setSelectedRowData(null);
  };

  const handleConfirmDeactivate = () => {
    // Add logic for actual deactivate operation using selectedRowData
    //console.log("Deactivate confirmed for:", selectedRowData);
    isActive.mutate({ id: selectedRowData._id , isActive: false })
    handleCloseDeactivateModal();
  };

  const handleConfirmDelete = () => {
    mutate(selectedRowData._id);
    handleCloseDeleteModal();
  };

  function handleActivate(rowData){
    setActivateModalOpen(true);
    setSelectedRowData(rowData);   
  };

  const handleConfirmActivate = () => {
    // Add logic for actual delete operation using selectedRowData
    //console.log("Delete confirmed for:", selectedRowData);
    isActive.mutate({ id: selectedRowData._id, isActive: true })
    handleCloseActivateModal();
  };



  const columnDefsAdmins = [
   // { field: "roles.User", headerName: "Role", flex: 1 },
    { field: "displayName", headerName: t('displayName'), sortable: true  },
   // { field: "username", headerName: "User Name", flex: 1 },
    { field: "email", headerName: t('email'), width: 300, sortable: true  },
    { field: "organization", headerName: t('organization'), flex: 1, sortable: true  },
    { field: "subOrganization", headerName: t("suborganization"), sortable: true  },
    { field: "town", headerName: t("town"), sortable: true  },
    { field: "isActive", headerName: t('active') },
    {
      headerName: t("actions"),
      cellRenderer: (params) => (
        <>
          <DeleteIcon
            onClick={() => handleDelete(params.data)}
            style={{ cursor: "pointer" }}
          />
      {params.data.isActive && 
          <DeactivateIcon
            onClick={() => handleDeactivate(params.data)}
            style={{ cursor: "pointer", color: '#ff7b7b', marginLeft: '10px' }}
          />}
          {!params.data.isActive && 
          <CheckCircleOutlineIcon onClick={() => handleActivate(params.data)} style={{ cursor: "pointer", color: 'green' , marginLeft: '10px' }} />}
        </>
      ),
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading admin data: {isError.message}</div>;
  }

  const rowData = data?.users?.filter(item => item.role === 'User') || [];

  return (
    <div>
       <TabAndSearchWrapper>
        <AdminTabs currentTab={currentTab} handleTabChange={handleTabChange} />
        <SearchInput
          searchText={searchTerm}
          handleSearchInputChange={handleSearchInput}
          localeText={{noRowsToShow: t('noRowsToShow')}}
        />
      </TabAndSearchWrapper>
      <div
        className="ag-theme-alpine"
        style={{ height: "80vh", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefsAdmins}
          
        ></AgGridReact>
      </div>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
      <DialogTitle>{t('deleteConfirmation')}</DialogTitle>
        <DialogContent>
          {t('areYouSureToDelete')}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>{t('cancel')}</Button>
          <Button onClick={handleConfirmDelete} color="error">
          {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Modal */}
      <Dialog open={deactivateModalOpen} onClose={handleCloseDeactivateModal}>
      <DialogTitle>{t('deactivateConfirmation')}</DialogTitle>
        <DialogContent>
        {t('areYouSureDeactivate')}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivateModal}>{t('cancel')}</Button>
          <Button onClick={handleConfirmDeactivate}>{t('deaktivate')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={activateModalOpen} onClose={handleCloseDeactivateModal}>
        <DialogTitle>{t('activateUser')}</DialogTitle>
        <DialogContent>
          {t('areYouSureActivate')}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActivateModal}>{t('cancel')}</Button>
          <Button onClick={handleConfirmActivate}>{t('activate')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AllUsersList;
